import React from 'react';
import { COLORS, TYPOGRAPHY } from '../../lib/styleGuide';

const ACCENT_GRAY = '#757575';
const WARNING_ORANGE = '#FF9800';
const SUCCESS_GREEN = '#4CAF50';

const perishableItems = [
  'Hot meals',
  'Refrigerated entrees',
  'Sauces',
  'Soups',
  'Dairy-based dishes',
  'Frozen items'
];

const commercialKitchenRequirements = [
  {
    requirement: 'Access to a certified commercial kitchen (ghost or commissary)',
    icon: '🏢'
  },
  {
    requirement: 'Apply for a food vendor license or establishment permit',
    icon: '📋'
  },
  {
    requirement: 'Pass a kitchen inspection by your local health department',
    icon: '🔍'
  },
  {
    requirement: 'Take a certified food safety course',
    icon: '🎓'
  },
  {
    requirement: 'Allowed to sell for pickup, delivery, or at markets',
    icon: '🚚'
  }
];

const personalChefRequirements = [
  {
    requirement: 'Known as a personal chef or meal prep service',
    icon: '👨‍🍳'
  },
  {
    requirement: 'You prepare meals in the client\'s kitchen — no commercial kitchen required',
    icon: '🏠'
  },
  {
    requirement: 'Still recommended: food safety certification & liability insurance',
    icon: '🛡️'
  },
  {
    requirement: 'Perfect for private dining, weekly meal prep, or small events',
    icon: '🎉'
  }
];

export default function PerishableMealsRequirementsStep({ onNext, onBack, isFirst, isLast }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, minHeight: '80vh' }}>
      {/* Header Section */}
      <div>
        <h2 style={{ fontWeight: 700, fontSize: TYPOGRAPHY.ONBOARDING.H2, margin: 0, color: '#222' }}>
          Let's talk about how you'll cook your meals
        </h2>
        <p style={{ color: ACCENT_GRAY, fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING, margin: '10px 0 0 0', lineHeight: 1.5 }}>
          If your food needs refrigeration or reheating, you'll need to follow special rules depending on where you plan to cook.
        </p>
      </div>

      {/* Perishable Items Callout */}
      <div style={{
        background: '#FFF8F0',
        borderRadius: 12,
        padding: 16,
        border: '1px solid #FFE0B2',
        borderLeft: `4px solid ${WARNING_ORANGE}`
      }}>
        <p style={{ color: '#E65100', fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, margin: 0, lineHeight: 1.5 }}>
          This includes hot meals, refrigerated entrees, sauces, soups, dairy-based dishes, and more.
        </p>
      </div>

      {/* Option 1: Commercial Kitchen */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <span style={{ fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING }}>🏢</span>
          <h3 style={{ fontWeight: 600, fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING, margin: 0, color: COLORS.PRIMARY_GRADIENT_START }}>
            Option 1: Use a Commercial Kitchen
          </h3>
        </div>
        
        <ul style={{ margin: '0 0 16px 0', paddingLeft: 20, color: '#222', fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, lineHeight: 1.6 }}>
          {commercialKitchenRequirements.map((item, index) => (
            <li key={index} style={{ marginBottom: 8 }}>
              <span style={{ marginRight: 8 }}>{item.icon}</span>
              <span dangerouslySetInnerHTML={{
                __html: item.requirement
                  .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
              }} />
            </li>
          ))}
        </ul>

        <div style={{
          background: '#FFF8F0',
          borderRadius: 12,
          padding: 16,
          border: '1px solid #FFE0B2',
          borderLeft: `4px solid ${WARNING_ORANGE}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING }}>💡</span>
            <p style={{ color: '#E65100', fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, margin: 0, lineHeight: 1.5 }}>
              Commercial co-op and ghost kitchens are common to find! Find one online that you schedule time in.
            </p>
          </div>
        </div>
      </div>

      {/* Option 2: Cook in Customer's Home */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <span style={{ fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING }}>👨‍🍳</span>
          <h3 style={{ fontWeight: 600, fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING, margin: 0, color: COLORS.PRIMARY_GRADIENT_START }}>
            Option 2: Cook in the Customer's Home
          </h3>
        </div>
        
        <ul style={{ margin: '0 0 16px 0', paddingLeft: 20, color: '#222', fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, lineHeight: 1.6 }}>
          {personalChefRequirements.map((item, index) => (
            <li key={index} style={{ marginBottom: 8 }}>
              <span style={{ marginRight: 8 }}>{item.icon}</span>
              <span dangerouslySetInnerHTML={{
                __html: item.requirement
                  .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
              }} />
            </li>
          ))}
        </ul>

        <div style={{
          background: '#FFF8F0',
          borderRadius: 12,
          padding: 16,
          border: '1px solid #FFE0B2',
          borderLeft: `4px solid ${WARNING_ORANGE}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING }}>✅</span>
            <p style={{ color: '#E65100', fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, margin: 0, lineHeight: 1.5 }}>
              This option has fewer legal barriers but you'll need to coordinate with clients and stay organized.
            </p>
          </div>
        </div>
      </div>

      {/* Heads Up Callout */}
      <div style={{
        background: '#F1F8E9',
        borderRadius: 12,
        padding: 16,
        border: '1px solid #C8E6C9',
        borderLeft: `4px solid ${SUCCESS_GREEN}`
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING }}>📋</span>
          <p style={{ color: '#2E7D32', fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, margin: 0, lineHeight: 1.5 }}>
            <strong>Heads up:</strong> You'll need to complete a few extra documentation steps before you start selling — but you can still finish setting up your shop now and come back to this later.
          </p>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        gap: 12, 
        marginTop: 'auto',
        paddingTop: 24
      }}>
        {!isFirst && (
          <button
            type="button"
            onClick={onBack}
            style={{
              background: '#fff',
              color: '#9CA3AF',
              border: 'none',
              borderRadius: 10,
              padding: '14px 32px',
              fontSize: TYPOGRAPHY.ONBOARDING.BUTTON_TEXT,
              fontWeight: 600,
              cursor: 'pointer',
              minHeight: 44,
              minWidth: 120,
              transition: 'all 0.2s',
            }}
            aria-label="Back"
          >
            Back
          </button>
        )}
        <button
          type="button"
          onClick={() => onNext && onNext()}
          style={{
            background: COLORS.PRIMARY_GRADIENT,
            color: '#fff',
            border: 'none',
            borderRadius: 10,
                          padding: '14px 32px',
            fontSize: TYPOGRAPHY.ONBOARDING.BUTTON_TEXT,
            fontWeight: 600,
            cursor: 'pointer',
            minHeight: 44,
            minWidth: 120,
            boxShadow: '0 1px 4px #0001',
            transition: 'background 0.2s',
            marginLeft: 'auto'
          }}
          aria-label="Continue to Shop Setup"
        >
          Continue to Shop Setup
        </button>
      </div>
    </div>
  );
} 